import { useEffect, useRef, useState } from 'react';
import TopBanner from '@/components/back-office/TopBanner';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import BOLayout from '@/pages/back-office/BOLayout';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from '@/components/Modal';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Pen from '@/assets/media/SVGComponents/Pen';
import Trash from '@/assets/media/SVGComponents/Trash';
import Drag from '@/assets/media/SVGComponents/Drag';

const MenuEdition = () => {
	const { selectedRestaurant } = useRestaurantsContext();
	const role = JSON.parse(localStorage.getItem('role'));
	const [menu, setMenu] = useState({});
	const [canSubmit, setCanSubmit] = useState(false);
	const [picture, setPicture] = useState({});
	const [preview, setPreview] = useState(null);
	const pictureRef = useRef(null);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [price, setPrice] = useState(0);
	const [steps, setSteps] = useState([]);
	const [initialSteps, setInitialSteps] = useState([]);
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedStep, setSelectedStep] = useState(null);
	const [quantity, setQuantity] = useState(1);
	const [vat, setVat] = useState('');
	const [vats, setVats] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [showHiddenProductsModal, setShowHiddenProductsModal] = useState(false);
	const [modalContext, setModalContext] = useState('add');
	const { restaurantId, menuId } = useParams();
	const [productsError, setProductsError] = useState('');
	const [hiddenProducts, setHiddenProducts] = useState([]);
	const [availableProducts, setAvailableProducts] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const navigate = useNavigate();

	useEffect(() => {
		if ((role && !['super-admin', 'admin'].includes(role)) || selectedRestaurant.is_no_limit) {
			toast.info('Zone interdite', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office');

			return;
		}
	}, [role]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const menuResponse = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/menu/${menuId}/view`
				);

				if (menuResponse.status === 200) {
					const menuData = menuResponse.data;
					setMenu(menuData);
					setName(menuData.name);
					setDescription(menuData.description);
					setPrice(menuData.price);
					setSteps(menuData.steps);
					setInitialSteps(menuData.steps);
					setPreview(menuData.picture);
					setVat(menuData.vat);
					setHiddenProducts(menuData.hidden_products || []);
				}

				const categoriesResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);

				if (categoriesResponse.status === 200) {
					setCategories(categoriesResponse.data);
				}

				const vatsResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/settings/vats/list`);

				if (vatsResponse.status === 200) {
					setVats(vatsResponse.data);
				}

				// Fetch available products
				const productsResponse = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/products/list`
				);

				if (productsResponse.status === 200) {
					setAvailableProducts(productsResponse.data);
				}
			} catch (error) {
				console.error(error);
			}
		};
		fetchData();
	}, [restaurantId, menuId]);

	useEffect(() => {
		if (
			(name && price && steps && steps.length > 0 && vat) ||
			(menu.name && menu.price && menu.steps && menu.steps.length > 0 && menu.vat)
		) {
			setCanSubmit(true);
			return;
		}

		setCanSubmit(false);
	}, [name, price, steps, menu, vat]);

	useEffect(() => {
		if (menu) {
			setPreview(menu?.picture);
			setName(menu.name);
			setDescription(menu.description);
			setPrice(menu.price);
		}
	}, [menu]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		const formData = new FormData();
		if (picture.name) formData.append('picture', picture);
		formData.append('name', name);
		formData.append('description', description);
		formData.append('price', price);
		formData.append('steps', JSON.stringify(steps));
		formData.append('vat', vat);
		formData.append('hiddenProducts', JSON.stringify(hiddenProducts));

		console.log('Valeurs envoyées:', {
			vat,
			menuVat: menu.vat?.id || menu.vat,
			finalVat: vat
		});

		let navigateToMenus = true;

		try {
			const response = await axios.put(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/menu/${menuId}/update`,
				formData
			);

			if (response.status === 200) {
				navigateToMenus = true;

				toast.success('Menu modifié avec succès', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			if (error?.response?.data?.code === 'PRODUCTS_NOT_FOUND') {
				setProductsError(error?.response?.data?.message);
				navigateToMenus = false;
			}

			toast.error('Une erreur est survenue lors de la modification du menu', {
				position: 'top-center',
				autoClose: 800,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		if (navigateToMenus) {
			setTimeout(() => navigate(`/back-office/restaurants/${selectedRestaurant?.id}/menu`), 1000);
		}
	};

	const handleOpenModal = (e, element, context) => {
		if (context === 'edit') {
			setModalContext(context);
		}

		setSelectedStep(element);
		setSelectedCategory(element.category_id);
		setQuantity(element.quantity);
		setShowModal(true);
	};

	const handleModalSubmit = () => {
		const quantityValue = Number(quantity);

		if (!selectedCategory || quantityValue === 0) {
			toast.info('Catégorie et quantité (supérieure  ou égale à 1) obligatoires', {
				position: 'top-center',
				autoClose: 3500,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			return;
		}

		if (modalContext === 'add') {
			setSteps([...steps, { category_id: selectedCategory, quantity: quantityValue }]);
		}

		if (modalContext === 'edit') {
			const updatedSteps = steps.map((step) => {
				if (step === selectedStep) {
					return { category_id: selectedCategory, quantity: quantityValue };
				}

				return step;
			});

			setSteps(updatedSteps);
			setModalContext('add');
		}

		setShowModal(false);
		setSelectedCategory('');
		setQuantity(1);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setSelectedCategory('');
		setQuantity(1);
	};

	const handlePicture = (e) => {
		setPicture(e.target.files[0]);
		setPreview(URL.createObjectURL(e.target.files[0]));
	};

	const onDragEnd = (result) => {
		if (!result.destination) {
			return;
		}

		const reorderedSteps = Array.from(steps);
		const [movedStep] = reorderedSteps.splice(result.source.index, 1);
		reorderedSteps.splice(result.destination.index, 0, movedStep);

		setSteps(reorderedSteps);

		// Check if the order has changed
		const isOrderChanged = JSON.stringify(initialSteps) !== JSON.stringify(reorderedSteps);
		setCanSubmit(isOrderChanged);
	};

	const handleHiddenProductsModalSubmit = () => {
		setShowHiddenProductsModal(false);
	};

	return (
		<BOLayout>
			{showHiddenProductsModal && (
				<Modal
					type={'info'}
					title={'Sélectionner les produits à masquer'}
					setShowModal={setShowHiddenProductsModal}
					handleSubmit={handleHiddenProductsModalSubmit}
					handleCloseModal={() => setShowHiddenProductsModal(false)}
				>
					<div className="max-h-[60vh] overflow-y-auto">
						<div className="sticky top-0 bg-white pb-4 mb-4 border-b">
							<input
								type="text"
								placeholder="Rechercher un produit..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
							/>
						</div>
						{categories.sort((a, b) => a.name.localeCompare(b.name)).map((category) => {
							const categoryProducts = availableProducts
								.filter(product => product.category_id === category.id)
								.filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()));
							if (categoryProducts.length === 0) return null;
							
							return (
								<div key={category.id} className="mb-6">
									<h3 className="font-bold text-lg mb-2 text-primary border-b pb-1">{category.name}</h3>
									<div className="grid grid-cols-2 gap-4">
										{categoryProducts
											.sort((a, b) => a.name.localeCompare(b.name))
											.map((product) => (
												<div key={product.id} className="flex items-center gap-2 mb-2">
													<input
														type="checkbox"
														id={`product-${product.id}`}
														checked={hiddenProducts.includes(product.id)}
														onChange={(e) => {
															if (e.target.checked) {
																setHiddenProducts([...hiddenProducts, product.id]);
															} else {
																setHiddenProducts(hiddenProducts.filter((id) => id !== product.id));
															}
														}}
													/>
													<label htmlFor={`product-${product.id}`} className="text-sm">{product.name}</label>
												</div>
										))}
									</div>
								</div>
							);
						})}
					</div>
				</Modal>
			)}

			<div className="mb-12">
				<TopBanner
					role={role}
					showButtons={'left'}
					showToggle={false}
					hasTopRightButton={false}
				/>
			</div>

			<h1 className="text-center text-3xl font-bold mb-4">Modification du menu</h1>

			{productsError && (
				<div className='grid grid-cols-2 gap-4 mb-4'>
					<span className="text-red-500">{productsError}</span>
					<span className="text-indigo-500">Rappel: il faut d'abord ajouter les produits dans ce restaurant pour pouvoir créer un menu et lui ajouter les étapes concernées</span>
				</div>
			)}

			<form className="flex flex-col items-center">
				<div className="grid grid-cols-2 gap-12">
					{/* Left */}
					<div>
						<div className="flex items-start gap-2">
							{preview && (
								<img
									src={preview}
									alt="picture"
									className="mb-4 border border-black p-2 rounded-lg w-[150px] h-[150px]"
								/>
							)}

							{showModal && (
								<div className="text-left">
									<Modal
										type={'info'}
										title={"Ajout d'une étape"}
										setShowModal={setShowModal}
										handleSubmit={handleModalSubmit}
										handleCloseModal={handleCloseModal}
									>
										<div className="flex items-center gap-4 mb-4">
											<label htmlFor="category">Sélectionnez une catégorie</label>
											<select
												id="category"
												name="category"
												className="border border-black p-2 rounded-lg"
												value={selectedCategory}
												onChange={(e) => setSelectedCategory(e.target.value)}
											>
												<option value="">Sélectionnez une catégorie</option>
												{categories
													.filter((category) => !steps.some((s) => s.category_id === category.id))
													.map((category) => (
														<option key={category.id} value={category.id}>
															{category.name}
														</option>
													))}
											</select>
										</div>

										<div className="flex items-center gap-4 mb-4">
											<label htmlFor="quantity">Nombre max. de produits</label>

											<input
												type="number"
												id="quantity"
												name="quantity"
												min={1}
												value={quantity}
												onChange={(e) => setQuantity(e.target.value)}
												className="p-2 w-1/5 border border-black rounded-lg"
											/>
										</div>
									</Modal>
								</div>
							)}

							<div
								className="mb-4 flex items-center border border-black p-2 rounded-lg w-3/5 text-sm hover:cursor-pointer"
								onClick={() => pictureRef.current.click()}
							>
								<input
									type="file"
									name="picture"
									id="picture"
									className="hidden"
									ref={pictureRef}
									onChange={handlePicture}
								/>

								<img src="/src/assets/media/pictures/import.png" alt="" width={30} className="mr-2" />
								<label className="hover:cursor-pointer">Téléchargez l'image de votre menu</label>
							</div>
						</div>

						<div className="flex flex-col justify-center gap-2">
							<div className="flex gap-2">
								<div className="flex flex-col">
									<label htmlFor="name" className="text-left pl-2">
										Nom
									</label>
									<input
										type="text"
										name="name"
										id="name"
										className="mb-4 border border-black p-2 rounded-lg"
										value={name}
										onChange={(e) => setName(e.target.value)}
									/>
								</div>

								<div className="flex flex-col w-2/5">
									<label htmlFor="price" className="text-left pl-2">
										Prix
									</label>
									<input
										type="number"
										name="price"
										id="price"
										min={0}
										step={0.01}
										className="mb-4 border border-black p-2 rounded-lg"
										value={price}
										onChange={(e) => setPrice(e.target.value)}
									/>
								</div>
							</div>

							<div className="flex flex-col">
								<label htmlFor="description" className="text-left pl-2">
									Description
								</label>
								<textarea
									cols={40}
									rows={5}
									name="description"
									id="description"
									className="mb-4 border border-black p-2 rounded-lg w-2/5"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
								></textarea>
							</div>
						</div>
					</div>

					{/* Right */}
					<div>
						<div className="flex flex-col mb-4">
							<button
								type="button"
								onClick={() => setShowModal(true)}
								className="text-left mb-4 transition duration-300 bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70 text-white px-4 py-2 rounded-lg w-2/5 text-center"
							>
								Ajouter une étape
							</button>

							<button
								type="button"
								onClick={() => setShowHiddenProductsModal(true)}
								className="text-left mb-4 transition duration-300 bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70 text-white px-4 py-2 rounded-lg w-2/5 text-center"
							>
								Sélectionner les produits à masquer
							</button>
						</div>

						{steps.length > 0 && (
							<DragDropContext onDragEnd={onDragEnd}>
								<Droppable droppableId="steps">
									{(provided) => (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
											className="flex flex-col justify-center items-start mb-4"
										>
											<p className="mb-2 lg:text-xl">Etapes:</p>
											{steps.map((step, index) => {
												return (
													<Draggable key={index} draggableId={`${index}`} index={index}>
														{(provided) => (
															<div
																ref={provided.innerRef}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																className="mb-2 flex items-center gap-2"
															>
																<span className="mr-4 flex items-center gap-2">
																	<Drag height={10} width={10} /> {index + 1}.
																</span>{' '}
																{step.quantity} {categories.find((cat) => step.category_id === cat.id)?.name}
																<div className="flex items-center gap-2">
																	<button type="button" onClick={(e) => handleOpenModal(e, step, 'edit')}>
																		<Pen width={30} height={30} color="#A2924A" />
																	</button>

																	<button
																		type="button"
																		onClick={() => setSteps(steps.filter((s) => s.category_id !== step.category_id))}
																	>
																		<Trash width={30} height={30} color="#A2924A" />
																	</button>
																</div>
															</div>
														)}
													</Draggable>
												);
											})}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>
						)}

						<div className="flex flex-col">
							<label htmlFor="vat" className="text-left pl-2">
								Taux de TVA en %
							</label>
							<select
								name="vat"
								id="vat"
								className="mb-4 border border-black p-2 rounded-lg"
								value={vat}
								onChange={(e) => setVat(e.target.value)}
							>
								<option value="">Sélectionnez une TVA</option>

								{vats.map((vat) => (
									<option key={vat.id} value={vat.id}>
										{vat.rate}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				<button
					type="submit"
					onClick={handleSubmit}
					className={`text-center transition duration-300 text-white px-4 py-2 rounded-lg ${canSubmit
						? 'bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70'
						: 'bg-gray-400 cursor-not-allowed'
						}`}
					disabled={!canSubmit}
				>
					Modifier la formule
				</button>
			</form>
		</BOLayout>
	);
};

export default MenuEdition;
